import React, { useEffect, useState, useCallback } from 'react';
import './ManageTakeProfitTracker.css';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

const CONFIGURED_TAKE_PROFIT_TYPE_OPTIONS = [
  'FIRST_EMA200_BAND_CROSS_AFTER_TREND_SHIFT',
  'FIRST_EMA50_BAND_CROSS_AFTER_TREND_SHIFT',
  'FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AFTER_TREND_SHIFT',
  'FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AT_EMA200_BAND_AFTER_TREND_SHIFT',
  'FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AT_EMA50_BAND_AFTER_TREND_SHIFT'
];

const matchesFilter = (value, filterValue) =>
  filterValue === '' || String(value ?? '').toLowerCase().includes(String(filterValue).toLowerCase());

function ManageTakeProfitTracker() {
  const { workspace } = useWorkspace();
  const withWorkspace = useCallback((row = {}) => ({
    ...row,
    exchange: row.exchange || workspace.exchange,
    marketType: row.marketType || workspace.marketType,
  }), [workspace]);
  const [dataList, setDataList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [coinDropdownOptions, setCoinDropdownOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'createdDate', direction: 'desc' });
  const [tpTypeOptions, setTpTypeOptions] = useState([]);
  const [tpTrackerSpecialIdentifierOptions, setTpTrackerSpecialIdentifierOptions] = useState([]);
  const [tpTrackerSourceOptions, setTpTrackerSourceOptions] = useState([]);

const [selectedIds, setSelectedIds] = useState(new Set());

const toggleSelect = (id) => {
  setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
};

const selectAllDisplayed = () => {
  setSelectedIds(new Set(filteredList.map(r => r.id)));
};

const clearSelection = () => setSelectedIds(new Set());

const allDisplayedSelected =
  filteredList.length > 0 && filteredList.every(r => selectedIds.has(r.id));

  useEffect(() => {
  setSelectedIds(prev => new Set([...prev].filter(id => filteredList.some(r => r.id === id))));
}, [filteredList]);



  const [filters, setFilters] = useState({
    symbol: '',
    timeFrame: '',
    tradeSide: '',
    tradeSetupRequestId: '',
    activeFlag: '',    
    takeProfitType: '',
    volumeBreakoutRequired: '',
    tpTrackerValue: '',
    trackerSpecialIdentifier: '',
    tpTrackerSource: '',
    takeProfitTrackerStartedOn: '',
    expectedCandleColorToClose: '',
    takeProfitTrackerStoppedOn: '',
    targetPercentage: '',
    closeSizePercentage: '',       
    signalRequestId: '',
    comments: '',
    createdBy: '',
    createdDate: '',
    modifiedBy: '',
    modifiedDate: '',
    indiaTimestamp: '',
    sgTimestamp: ''
  });

  const [newRow, setNewRow] = useState({
    symbol: '',
    timeFrame: '',
    tradeSide: '',
    tradeSetupRequestId: '',
    activeFlag: 'Y',    
    takeProfitType: '',
    volumeBreakoutRequired: false,
    tpTrackerValue: '',
    trackerSpecialIdentifier: '',
    tpTrackerSource: '',    
    takeProfitTrackerStartedOn: '',
    expectedCandleColorToClose: '',
    takeProfitTrackerStoppedOn: '',
    targetPercentage: '',
    closeSizePercentage: '',
    signalRequestId: '',
    comments: '',
    createdBy: '',
    modifiedBy: ''
  });

  const [editIndex, setEditIndex] = useState(null);
  const [editRowData, setEditRowData] = useState(null);

  const timeFrameOptions = ['5', '15', '30', '60', '240', '480', '720', '1440'];


  const handleSort = (key) => {
  let direction = 'asc';
  if (sortConfig.key === key && sortConfig.direction === 'asc') {
    direction = 'desc';
  }
  setSortConfig({ key, direction });

  const sorted = [...filteredList].sort((a, b) => {
    let aVal = a[key] ?? '';
    let bVal = b[key] ?? '';

    // Numeric fields
    if (['timeFrame'].includes(key)) {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
    }

    // Date fields
    if (['takeProfitTrackerStartedOn', 'takeProfitTrackerStoppedOn', 'createdDate', 'modifiedDate', 'indiaTimestamp', 'sgTimestamp'].includes(key)) {
    aVal = aVal ? new Date(aVal).getTime() : 0;
    bVal = bVal ? new Date(bVal).getTime() : 0;
    }


    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  setFilteredList(sorted);
};


const getSortIcon = (key) => {
  if (sortConfig.key === key) {
    return sortConfig.direction === 'asc' ? ' ^' : ' v';
  }
  return '';
};

  const fetchRecords = useCallback(async () => {
    try {
      const response = await fetch(
        buildScopedApiUrl('/api/bot/ui/get/getAllTakeProfitTrackerRecords', workspace),
        {
          headers: getApiHeaders()
        }
      );
      const data = await response.json();
      const normalizedData = ensureArray(data).map((item) => withWorkspace(item));
      setDataList(normalizedData);
      setFilteredList(normalizedData);

      const uniqueTypes = Array.from(
        new Set([
          ...CONFIGURED_TAKE_PROFIT_TYPE_OPTIONS,
          ...normalizedData
            .map(r => r?.takeProfitType)
            .filter(Boolean)
        ])
      ).sort((a, b) => a.localeCompare(b));
      setTpTypeOptions(uniqueTypes);

      const uniqueTpTrackerSpecialIdentifier = Array.from(
        new Set(
          normalizedData
            .map(r => r?.trackerSpecialIdentifier)
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));
      setTpTrackerSpecialIdentifierOptions(uniqueTpTrackerSpecialIdentifier);

      const uniqueTpTrackerSource = Array.from(
        new Set(
          normalizedData
            .map(r => r?.tpTrackerSource)
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));
      setTpTrackerSourceOptions(uniqueTpTrackerSource);
    } catch (error) {
      console.error('Take profit tracker fetch failed:', error);
      setDataList([]);
      setFilteredList([]);
      setTpTypeOptions([]);
      setTpTrackerSpecialIdentifierOptions([]);
      setTpTrackerSourceOptions([]);
    }
  }, [withWorkspace, workspace]);

  const fetchCoinDropdown = useCallback(async () => {
    try {
      const response = await fetch(
        buildScopedApiUrl('/api/bot/ui/get/getRecentActiveAlertSymbols', workspace),
        {
          headers: getApiHeaders()
        });
      const coins = await response.json();
      setCoinDropdownOptions(ensureArray(coins));
    } catch (err) {
      console.error('Failed to fetch coin dropdown list:', err);
    }
  }, [workspace]);

  useEffect(() => {
    fetchRecords();
    fetchCoinDropdown();
  }, [fetchCoinDropdown, fetchRecords]);


  

  const applyFilters = useCallback(() => {
    let filtered = dataList.filter(
      (item) =>
        matchesFilter(item.symbol, filters.symbol) &&
        matchesFilter(item.timeFrame, filters.timeFrame) &&
        matchesFilter(item.tradeSide, filters.tradeSide) &&
        matchesFilter(item.tradeSetupRequestId, filters.tradeSetupRequestId) &&
        matchesFilter(item.activeFlag, filters.activeFlag) &&
        matchesFilter(item.takeProfitType, filters.takeProfitType) &&
        matchesFilter(String(!!item.volumeBreakoutRequired), filters.volumeBreakoutRequired) &&
        matchesFilter(item.tpTrackerValue, filters.tpTrackerValue) &&
        matchesFilter(item.trackerSpecialIdentifier, filters.trackerSpecialIdentifier) &&
        matchesFilter(item.tpTrackerSource, filters.tpTrackerSource) &&
        matchesFilter(item.takeProfitTrackerStartedOn, filters.takeProfitTrackerStartedOn) &&
        matchesFilter(item.expectedCandleColorToClose, filters.expectedCandleColorToClose) &&
        matchesFilter(item.takeProfitTrackerStoppedOn, filters.takeProfitTrackerStoppedOn) &&
        matchesFilter(item.targetPercentage, filters.targetPercentage) &&
        matchesFilter(item.closeSizePercentage, filters.closeSizePercentage) &&
        matchesFilter(item.signalRequestId, filters.signalRequestId) &&
        matchesFilter(item.comments, filters.comments) &&
        matchesFilter(item.createdBy, filters.createdBy) &&
        matchesFilter(item.createdDate, filters.createdDate) &&
        matchesFilter(item.modifiedBy, filters.modifiedBy) &&
        matchesFilter(item.modifiedDate, filters.modifiedDate) &&
        matchesFilter(item.indiaTimestamp, filters.indiaTimestamp) &&
        matchesFilter(item.sgTimestamp, filters.sgTimestamp)
    );
    setFilteredList(filtered);
  }, [dataList, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // ✅ Automatically apply default sort when data first loads
useEffect(() => {
  if (filteredList.length === 0) return;

  // Call your existing handleSort manually for the default key
  handleSort(sortConfig.key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [filteredList.length]);


  const handleAdd = async () => {
    await fetch(
      buildScopedApiUrl('/api/bot/ui/get/saveTakeProfitTrackers', workspace),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
        body: JSON.stringify(withWorkspace(newRow))
      }
    );
    setNewRow({
      symbol: '',
      timeFrame: '',
      tradeSide: '',
      tradeSetupRequestId: '',
      activeFlag: '',
      takeProfitType: '',
      volumeBreakoutRequired: false,
      tpTrackerValue: '',      
      trackerSpecialIdentifier: '',
      tpTrackerSource: '',
      takeProfitTrackerStartedOn: '',
      expectedCandleColorToClose: '',
      takeProfitTrackerStoppedOn: '',
      targetPercentage: '',
      closeSizePercentage: '',
      signalRequestId: '',
      comments: ''

    });
    fetchRecords();
  };

  const handleSave = async () => {
    await fetch(
      buildScopedApiUrl('/api/bot/ui/get/saveTakeProfitTrackers', workspace),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
        body: JSON.stringify(withWorkspace(editRowData))
      }
    );
    setEditIndex(null);
    fetchRecords();
  };

  const handleDelete = async (id) => {
    await fetch(
      buildScopedApiUrl('/api/bot/ui/get/deleteTakeProfitTrackerById', workspace, { id }),
      { method: 'DELETE' ,
        headers: getApiHeaders()}
    );
    fetchRecords();
  };

  const deleteByIds = async (ids) => {
  if (!ids || ids.length === 0) return;
  await fetch(
    buildScopedApiUrl('/api/bot/ui/get/deleteTakeProfitTrackersByList', workspace),
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
      body: JSON.stringify(ids)
    }
  );
  clearSelection();
  fetchRecords();
};

const handleDeleteSelected = async () => {
  const ids = Array.from(selectedIds);
  if (ids.length === 0) return;
  if (!window.confirm(`Delete ${ids.length} selected take profit tracker record(s)? This cannot be undone.`)) return;
  await deleteByIds(ids);
};

const handleDeleteAllFiltered = async () => {
  const ids = filteredList.map(r => r.id).filter(Boolean);
  if (ids.length === 0) return;
  if (!window.confirm(`Delete ALL ${ids.length} filtered take profit tracker record(s)? This cannot be undone.`)) return;
  await deleteByIds(ids);
};

const handleDeleteOne = async (row) => {
  if (!window.confirm(`Delete take profit tracker for ${row.symbol || 'this record'}? This cannot be undone.`)) return;
  await handleDelete(row.id);
};


  const clearFilters = () => {
    setFilters({
      symbol: '',
      timeFrame: '',
      tradeSide: '',
      tradeSetupRequestId: '',      
      activeFlag: '',
      takeProfitType: '',
      volumeBreakoutRequired: '',
      tpTrackerValue: '',
      trackerSpecialIdentifier: '',
      tpTrackerSource: '',
      takeProfitTrackerStartedOn: '',
      expectedCandleColorToClose: '',
      takeProfitTrackerStoppedOn: '',
      targetPercentage: '',
      closeSizePercentage: '',
      signalRequestId: '',
      comments: '',
      createdBy: '',
      createdDate: '',
      modifiedBy: '',
      modifiedDate: '',
      indiaTimestamp: '',
      sgTimestamp: ''
    });
  };

  return (
    <div className="take-profit-page">
      <h2 className="page-title">Manage Take Profit Trackers</h2>
      <div className="filter-actions">
        <button className="btn professional-clear" onClick={clearFilters}>
          <span className="professional-clear__icon" aria-hidden="true" />
          Clear Filters
        </button>
         <button
          className="btn danger"
          onClick={handleDeleteSelected}
          disabled={selectedIds.size === 0}
          style={{ marginLeft: 8 }}
        >
          Delete Selected
        </button>
        <button
          className="btn danger"
          onClick={handleDeleteAllFiltered}
          disabled={filteredList.length === 0}
          style={{ marginLeft: 8 }}
        >
          Delete All Filtered
        </button>
        <button
          className="btn select-all-filtered-btn"
          onClick={() => (allDisplayedSelected ? clearSelection() : selectAllDisplayed())}
          style={{ marginLeft: 8 }}
        >
          {allDisplayedSelected ? 'Unselect All' : 'Select All Filtered'}
        </button>
      </div>

      <div className="table-wrapper">
        <table className="take-profit-table">
          <thead>
            <tr>
                <th>S.No</th>
                 <th>
                  <input
                    type="checkbox"
                    checked={allDisplayedSelected}
                    onChange={(e) => (e.target.checked ? selectAllDisplayed() : clearSelection())}
                  />
                </th>
                <th onClick={() => handleSort('symbol')}>Symbol {getSortIcon('symbol')}</th>
                <th onClick={() => handleSort('timeFrame')}>TimeFrame {getSortIcon('timeFrame')}</th>
                <th onClick={() => handleSort('tradeSide')}>Trade Side {getSortIcon('tradeSide')}</th>
                <th onClick={() => handleSort('tradeSetupRequestId')}>Trade Setup Id{getSortIcon('tradeSetupRequestId')}</th>     
                <th onClick={() => handleSort('activeFlag')}>Active {getSortIcon('activeFlag')}</th>                
                <th onClick={() => handleSort('takeProfitType')}>Take Profit Type {getSortIcon('takeProfitType')}</th>
                <th onClick={() => handleSort('volumeBreakoutRequired')}>Volume Breakout {getSortIcon('volumeBreakoutRequired')}</th>
                <th onClick={() => handleSort('tpTrackerValue')}>TP Tracker Value {getSortIcon('tpTrackerValue')}</th>    
                <th onClick={() => handleSort('trackerSpecialIdentifier')}>Tracker Special Identifier {getSortIcon('trackerSpecialIdentifier')}</th>    
                <th onClick={() => handleSort('tpTrackerSource')}>TP Tracker Source {getSortIcon('tpTrackerSource')}</th>                                              
                <th onClick={() => handleSort('takeProfitTrackerStartedOn')}>Started On {getSortIcon('takeProfitTrackerStartedOn')}</th>
                <th onClick={() => handleSort('expectedCandleColorToClose')}>Expected Candle Color {getSortIcon('expectedCandleColorToClose')}</th>
                <th onClick={() => handleSort('takeProfitTrackerStoppedOn')}>Stopped On {getSortIcon('takeProfitTrackerStoppedOn')}</th>
                <th onClick={() => handleSort('targetPercentage')}>Target % {getSortIcon('targetPercentage')}</th>    
                <th onClick={() => handleSort('closeSizePercentage')}>closeSize % {getSortIcon('closeSizePercentage')}</th>                    
                <th onClick={() => handleSort('signalRequestId')}>SignalRequest Id{getSortIcon('signalRequestId')}</th>   
                <th onClick={() => handleSort('comments')}>Comments{getSortIcon('comments')}</th>                                           
                <th onClick={() => handleSort('createdBy')}>Created By {getSortIcon('createdBy')}</th>
                <th onClick={() => handleSort('createdDate')}>Created Date {getSortIcon('createdDate')}</th>
                <th onClick={() => handleSort('modifiedBy')}>Modified By {getSortIcon('modifiedBy')}</th>
                <th onClick={() => handleSort('modifiedDate')}>Modified Date {getSortIcon('modifiedDate')}</th>
                <th onClick={() => handleSort('indiaTimestamp')}>India Time {getSortIcon('indiaTimestamp')}</th>
                <th onClick={() => handleSort('sgTimestamp')}>SG Time {getSortIcon('sgTimestamp')}</th>
                <th>Actions</th>

            </tr>

            <tr className="filter-row">
              <th></th>
              <th></th>
              <th>
                <select
                  value={filters.symbol}
                  onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
                >
                  <option value="">All</option>
                  {coinDropdownOptions.map((coin, idx) => (
                    <option key={idx} value={coin}>
                      {coin}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  value={filters.timeFrame}
                  onChange={(e) => setFilters({ ...filters, timeFrame: e.target.value })}
                >
                  <option value="">All</option>
                  {timeFrameOptions.map((tf, idx) => (
                    <option key={idx} value={tf}>
                      {tf}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  value={filters.tradeSide}
                  onChange={(e) => setFilters({ ...filters, tradeSide: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="open_long">open_long</option>
                  <option value="open_short">open_short</option>
                </select>
              </th>
              <th>
                <input
                  value={filters.tradeSetupRequestId}
                  onChange={(e) => setFilters({ ...filters, tradeSetupRequestId: e.target.value })}
                  placeholder="Filter setup id"
                />
              </th>
              <th>
                <select
                  value={filters.activeFlag}
                  onChange={(e) => setFilters({ ...filters, activeFlag: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </th>   
              <th>
                <select
                  value={filters.takeProfitType}
                  onChange={(e) => setFilters({ ...filters, takeProfitType: e.target.value })}
                >
                  <option value="">All</option>
                  {tpTypeOptions.map((type, idx) => (
                    <option key={idx} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  value={filters.volumeBreakoutRequired}
                  onChange={(e) => setFilters({ ...filters, volumeBreakoutRequired: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              </th>
              <th>
                <input
                  value={filters.tpTrackerValue}
                  onChange={(e) => setFilters({ ...filters, tpTrackerValue: e.target.value })}
                  placeholder="Filter value"
                />
              </th>
               <th>
                <select
                  value={filters.trackerSpecialIdentifier}
                  onChange={(e) => setFilters({ ...filters, trackerSpecialIdentifier: e.target.value })}
                >
                  <option value="">All</option>
                  {tpTrackerSpecialIdentifierOptions.map((type, idx) => (
                    <option key={idx} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </th>      
                <th>
                <select
                  value={filters.tpTrackerSource}
                  onChange={(e) => setFilters({ ...filters, tpTrackerSource: e.target.value })}
                >
                  <option value="">All</option>
                  {tpTrackerSourceOptions.map((type, idx) => (
                    <option key={idx} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <input
                  value={filters.takeProfitTrackerStartedOn}
                  onChange={(e) => setFilters({ ...filters, takeProfitTrackerStartedOn: e.target.value })}
                  placeholder="Filter started"
                />
              </th>
              <th>
                <input
                  value={filters.expectedCandleColorToClose}
                  onChange={(e) => setFilters({ ...filters, expectedCandleColorToClose: e.target.value })}
                  placeholder="Filter color"
                />
              </th>
              <th>
                <input
                  value={filters.takeProfitTrackerStoppedOn}
                  onChange={(e) => setFilters({ ...filters, takeProfitTrackerStoppedOn: e.target.value })}
                  placeholder="Filter stopped"
                />
              </th>
              <th>
                <input
                  value={filters.targetPercentage}
                  onChange={(e) => setFilters({ ...filters, targetPercentage: e.target.value })}
                  placeholder="Filter target"
                />
              </th>
              <th>
                <input
                  value={filters.closeSizePercentage}
                  onChange={(e) => setFilters({ ...filters, closeSizePercentage: e.target.value })}
                  placeholder="Filter close size"
                />
              </th>
              <th>
                <input
                  value={filters.signalRequestId}
                  onChange={(e) => setFilters({ ...filters, signalRequestId: e.target.value })}
                  placeholder="Filter signal id"
                />
              </th>
              <th>
                <input
                  value={filters.comments}
                  onChange={(e) => setFilters({ ...filters, comments: e.target.value })}
                  placeholder="Filter comments"
                />
              </th>
              <th>
                <input
                  value={filters.createdBy}
                  onChange={(e) => setFilters({ ...filters, createdBy: e.target.value })}
                  placeholder="Filter created by"
                />
              </th>
              <th>
                <input
                  value={filters.createdDate}
                  onChange={(e) => setFilters({ ...filters, createdDate: e.target.value })}
                  placeholder="Filter created"
                />
              </th>
              <th>
                <input
                  value={filters.modifiedBy}
                  onChange={(e) => setFilters({ ...filters, modifiedBy: e.target.value })}
                  placeholder="Filter modified by"
                />
              </th>
              <th>
                <input
                  value={filters.modifiedDate}
                  onChange={(e) => setFilters({ ...filters, modifiedDate: e.target.value })}
                  placeholder="Filter modified"
                />
              </th>
              <th>
                <input
                  value={filters.indiaTimestamp}
                  onChange={(e) => setFilters({ ...filters, indiaTimestamp: e.target.value })}
                  placeholder="Filter India time"
                />
              </th>
              <th>
                <input
                  value={filters.sgTimestamp}
                  onChange={(e) => setFilters({ ...filters, sgTimestamp: e.target.value })}
                  placeholder="Filter SG time"
                />
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredList.map((row, index) => (
              <tr key={row.id}>
                {editIndex === index ? (
                  <>
                  <td>{index + 1}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </td>
                    <td>
                      <select
                        value={editRowData.symbol}
                        onChange={(e) =>
                          setEditRowData({ ...editRowData, symbol: e.target.value })
                        }
                      >
                        <option value="">Select</option>
                        {coinDropdownOptions.map((coin, idx) => (
                          <option key={idx} value={coin}>
                            {coin}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={editRowData.timeFrame}
                        onChange={(e) =>
                          setEditRowData({ ...editRowData, timeFrame: e.target.value })
                        }
                      >
                        <option value="">Select</option>
                        {timeFrameOptions.map((tf, idx) => (
                          <option key={idx} value={tf}>
                            {tf}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={editRowData.tradeSide}
                        onChange={(e) =>
                          setEditRowData({ ...editRowData, tradeSide: e.target.value })
                        }
                      >
                        <option value="open_long">open_long</option>
                        <option value="open_short">open_short</option>
                      </select>
                    </td>
                    <td>
                      <input
                        value={editRowData.tradeSetupRequestId}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            tradeSetupRequestId: e.target.value
                          })
                        }
                      />
                    </td>        
                    <td>
                      <select
                        value={editRowData.activeFlag}
                        onChange={(e) =>
                          setEditRowData({ ...editRowData, activeFlag: e.target.value })
                        }
                      >
                        <option value="Y">Y</option>
                        <option value="N">N</option>
                      </select>
                    </td>     
                    <td>
                      <select
                        value={editRowData.takeProfitType}
                        onChange={(e) =>
                          setEditRowData({ ...editRowData, takeProfitType: e.target.value })
                        }
                      >
                        <option value="">Select</option>
                        {tpTypeOptions.map((type, idx) => (
                          <option key={idx} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!editRowData.volumeBreakoutRequired}
                        onChange={(e) =>
                          setEditRowData({ ...editRowData, volumeBreakoutRequired: e.target.checked })
                        }
                      />
                    </td>                     <td>
                      <input
                        value={editRowData.tpTrackerValue}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            tpTrackerValue: e.target.value
                          })
                        }
                      />
                    </td>    
                     <td>
                      <input
                        value={editRowData.trackerSpecialIdentifier}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            trackerSpecialIdentifier: e.target.value
                          })
                        }
                      />
                    </td> 
                     <td>
                      <input
                        value={editRowData.tpTrackerSource}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            tpTrackerSource: e.target.value
                          })
                        }
                      />
                    </td>                                                                                                 
                    <td>
                      <input
                        value={editRowData.takeProfitTrackerStartedOn}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            takeProfitTrackerStartedOn: e.target.value
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={editRowData.expectedCandleColorToClose}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            expectedCandleColorToClose: e.target.value
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={editRowData.takeProfitTrackerStoppedOn}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            takeProfitTrackerStoppedOn: e.target.value
                          })
                        }
                      />
                    </td>

                     <td>
                      <input
                        value={editRowData.targetPercentage}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            targetPercentage: e.target.value
                          })
                        }
                      />
                    </td> 

                     <td>
                      <input
                        value={editRowData.closeSizePercentage}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            closeSizePercentage: e.target.value
                          })
                        }
                      />
                    </td> 

                     <td>
                      <input
                        value={editRowData.signalRequestId}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            signalRequestId: e.target.value
                          })
                        }
                      />
                    </td>     
                     <td>
                      <input
                        value={editRowData.comments}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            comments: e.target.value
                          })
                        }
                        />
                    </td>     
                    <td>
                      <input
                        value={editRowData.createdBy ?? ''}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            createdBy: e.target.value
                          })
                        }
                      />
                    </td>                      

                    <td>{row.createdDate || '-'}</td>
                    <td>
                      <input
                        value={editRowData.modifiedBy ?? ''}
                        onChange={(e) =>
                          setEditRowData({
                            ...editRowData,
                            modifiedBy: e.target.value
                          })
                        }
                      />
                    </td>
                    <td>{row.modifiedDate || '-'}</td>
                    <td>{row.indiaTimestamp || '-'}</td>
                    <td>{row.sgTimestamp || '-'}</td>

                    <td>
                      <button className="btn btn--save" onClick={handleSave}>
                        Save
                      </button>
                      <button className="btn btn--cancel" onClick={() => setEditIndex(null)}>
                        Cancel
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                   <td>{index + 1}</td>
                   <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleSelect(row.id)}
                      />
                    </td>
                    <td>{row.symbol}</td>
                    <td>{row.timeFrame}</td>
                    <td>{row.tradeSide}</td>
                    <td>{row.tradeSetupRequestId}</td>       
                    <td>{row.activeFlag}</td>
                    <td>{row.takeProfitType}</td>
                    <td>{String(row.volumeBreakoutRequired ?? false)}</td>
                    <td>{row.tpTrackerValue}</td>       
                    <td>{row.trackerSpecialIdentifier}</td>  
                    <td>{row.tpTrackerSource}</td>                                                    
                    <td>{row.takeProfitTrackerStartedOn}</td>
                    <td>{row.expectedCandleColorToClose}</td>
                    <td>{row.takeProfitTrackerStoppedOn}</td>
                    <td>{row.targetPercentage}</td>    
                    <td>{row.closeSizePercentage}</td>    
                    <td>{row.signalRequestId}</td>   
                    <td>{row.comments}</td>                                                                    
                    <td>{row.createdBy || '-'}</td>
                    <td>{row.createdDate || '-'}</td>
                    <td>{row.modifiedBy || '-'}</td>
                    <td>{row.modifiedDate || '-'}</td>
                    <td>{row.indiaTimestamp || '-'}</td>
                    <td>{row.sgTimestamp || '-'}</td>
                    <td>
                    <button className="btn btn--edit" onClick={() => { setEditIndex(index); setEditRowData(row); }}>Edit</button>
                    <button className="btn btn--delete" onClick={() => handleDeleteOne(row)}>Delete</button>
                    </td>

                  </>
                )}
              </tr>
            ))}

            <tr>
              <td></td>
              <td></td>
              <td>
                <select
                  value={newRow.symbol}
                  onChange={(e) => setNewRow({ ...newRow, symbol: e.target.value })}
                >
                  <option value="">Select</option>
                  {coinDropdownOptions.map((coin, idx) => (
                    <option key={idx} value={coin}>
                      {coin}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={newRow.timeFrame}
                  onChange={(e) => setNewRow({ ...newRow, timeFrame: e.target.value })}
                >
                  <option value="">Select</option>
                  {timeFrameOptions.map((tf, idx) => (
                    <option key={idx} value={tf}>
                      {tf}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={newRow.tradeSide}
                  onChange={(e) => setNewRow({ ...newRow, tradeSide: e.target.value })}
                >
                  <option value="">Select</option>
                  <option value="open_long">open_long</option>
                  <option value="open_short">open_short</option>
                </select>
              </td>
              <td>
                <input
                  value={newRow.tradeSetupRequestId}
                  onChange={(e) =>
                    setNewRow({ ...newRow, tradeSetupRequestId: e.target.value })
                  }
                />
              </td>  
              <td>
                <select
                  value={newRow.activeFlag}
                  onChange={(e) => setNewRow({ ...newRow, activeFlag: e.target.value })}
                >
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </td>     
              <td>
                <select
                  value={newRow.takeProfitType}
                  onChange={(e) => setNewRow({ ...newRow, takeProfitType: e.target.value })}
                >
                  <option value="">Select</option>
                  {tpTypeOptions.map((type, idx) => (
                    <option key={idx} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={!!newRow.volumeBreakoutRequired}
                  onChange={(e) => setNewRow({ ...newRow, volumeBreakoutRequired: e.target.checked })}
                />
              </td>              <td>
                <input
                  value={newRow.tpTrackerValue}
                  onChange={(e) =>
                    setNewRow({ ...newRow, tpTrackerValue: e.target.value })
                  }
                />
              </td> 
              <td>
                <input
                  value={newRow.trackerSpecialIdentifier}
                  onChange={(e) =>
                    setNewRow({ ...newRow, trackerSpecialIdentifier: e.target.value })
                  }
                />
              </td>     
              <td>
                <input
                  value={newRow.tpTrackerSource}
                  onChange={(e) =>
                    setNewRow({ ...newRow, tpTrackerSource: e.target.value })
                  }
                />
              </td>                                                                 
              <td>
                <input
                  value={newRow.takeProfitTrackerStartedOn}
                  onChange={(e) =>
                    setNewRow({ ...newRow, takeProfitTrackerStartedOn: e.target.value })
                  }
                />
              </td>
              <td>
                <input
                  value={newRow.expectedCandleColorToClose}
                  onChange={(e) =>
                    setNewRow({ ...newRow, expectedCandleColorToClose: e.target.value })
                  }
                />
              </td>
              <td>
                <input
                  value={newRow.takeProfitTrackerStoppedOn}
                  onChange={(e) =>
                    setNewRow({ ...newRow, takeProfitTrackerStoppedOn: e.target.value })
                  }
                />
              </td>

              <td>
                <input
                  value={newRow.targetPercentage}
                  onChange={(e) =>
                    setNewRow({ ...newRow, targetPercentage: e.target.value })
                  }
                />
              </td>        

              <td>
                <input
                  value={newRow.closeSizePercentage}
                  onChange={(e) =>
                    setNewRow({ ...newRow, closeSizePercentage: e.target.value })
                  }
                />
              </td>        
    
              <td>
                <input
                  value={newRow.signalRequestId}
                  onChange={(e) =>
                    setNewRow({ ...newRow, signalRequestId: e.target.value })
                  }
                />
              </td>     

              <td>
                <input
                  value={newRow.comments}
                  onChange={(e) =>
                    setNewRow({ ...newRow, comments: e.target.value })
                  }
                />
              </td>                   
              <td>
                <input
                  value={newRow.createdBy}
                  onChange={(e) =>
                    setNewRow({ ...newRow, createdBy: e.target.value })
                  }
                />
              </td>      

                <td>{newRow.createdDate || '-'}</td>
              <td>
                <input
                  value={newRow.modifiedBy}
                  onChange={(e) =>
                    setNewRow({ ...newRow, modifiedBy: e.target.value })
                  }
                />
              </td>   
                <td>{newRow.modifiedDate || '-'}</td>
                <td>{newRow.indiaTimestamp || '-'}</td>
                <td>{newRow.sgTimestamp || '-'}</td>
              <td>
                <button className="btn btn--add" onClick={handleAdd}>
                  Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="take-profit-record-count">
        Showing {filteredList.length} of {dataList.length} records
      </div>
    </div>
  );
}

export default ManageTakeProfitTracker;




